import React from "react";
import {
  Skeleton,
  KpiSkeleton,
  TableSkeleton,
  ChartSkeleton,
  CardListSkeleton,
  FormSkeleton,
} from "../../../components/Skeleton";

// Card-body placeholders for M-SMART pages. They sit inside the page's existing
// header + tab card, so only the body area shimmers while data loads.

const Wrap = ({ children }) => (
  <div className="text-start" role="status" aria-live="polite" aria-label="Loading">
    {children}
    <span className="visually-hidden">Loading…</span>
  </div>
);

const ControlBar = ({ buttons = 3 }) => (
  <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
    <Skeleton width={130} height={38} radius={10} />
    <Skeleton width={260} height={38} radius={10} style={{ flex: "1 1 200px", maxWidth: 420 }} />
    <div className="ms-auto d-flex gap-2">
      {Array.from({ length: buttons }).map((_, i) => (
        <Skeleton key={i} width={96} height={38} radius={10} />
      ))}
    </div>
  </div>
);

const DateBar = () => (
  <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
    {[70, 80, 90, 80, 100].map((w, i) => (
      <Skeleton key={i} width={w} height={30} radius={999} />
    ))}
    <Skeleton width={220} height={38} radius={10} className="ms-auto" />
  </div>
);

export const LeadCardsSkeleton = ({ count = 6 }) => (
  <div className="row mt-3">
    {Array.from({ length: count }).map((_, i) => (
      <div className="col-lg-6 col-md-12" key={i}>
        <div className="card mb-3 border-light shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center">
            <Skeleton width="40%" height={18} />
            <Skeleton width={60} height={20} radius={20} />
          </div>
          <div className="card-body bg-light">
            <Skeleton width="50%" height={15} className="mb-2" />
            <Skeleton width="80%" height={15} />
          </div>
          <div className="card-footer d-flex justify-content-between">
            <Skeleton width="30%" height={12} />
            <Skeleton width={40} height={25} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const LeadsBodySkeleton = () => (
  <Wrap>
    <ControlBar />
    <div className="d-flex justify-content-end gap-2 my-2">
      <Skeleton width={32} height={28} />
      <Skeleton width={60} height={28} />
      <Skeleton width={32} height={28} />
    </div>
    <LeadCardsSkeleton />
  </Wrap>
);

export const LeadListBodySkeleton = () => (
  <Wrap>
    <ControlBar buttons={2} />
    <TableSkeleton rows={10} cols={6} withToolbar={false} />
  </Wrap>
);

export const FollowUpBodySkeleton = () => (
  <Wrap>
    <DateBar />
    <KpiSkeleton count={4} />
    <TableSkeleton rows={8} cols={5} withToolbar={false} />
  </Wrap>
);

export const SummaryBodySkeleton = ({ withDateBar = true }) => (
  <Wrap>
    {withDateBar && <DateBar />}
    <div className="row g-3 mb-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="col-12 col-md-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
            <div className="card-body d-flex align-items-center gap-3">
              <Skeleton width={44} height={44} radius={12} style={{ flex: "0 0 auto" }} />
              <div className="flex-grow-1">
                <Skeleton width="50%" height={10} className="mb-2" />
                <Skeleton width="75%" height={22} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="row g-3">
      <div className="col-12 col-lg-8"><ChartSkeleton height={220} /></div>
      <div className="col-12 col-lg-4"><ChartSkeleton height={220} /></div>
    </div>
  </Wrap>
);

export const ActivityBodySkeleton = () => (
  <Wrap>
    <DateBar />
    <CardListSkeleton count={5} />
  </Wrap>
);

export const TeamBodySkeleton = () => (
  <Wrap>
    <div className="d-flex flex-wrap gap-2 mb-3">
      <Skeleton width={240} height={38} radius={10} />
      <Skeleton width={140} height={38} radius={10} className="ms-auto" />
    </div>
    <TableSkeleton rows={8} cols={5} withToolbar={false} />
  </Wrap>
);

export const FormBuilderSkeleton = () => (
  <Wrap>
    <div className="d-flex justify-content-between mb-3">
      <Skeleton width={180} height={22} />
      <Skeleton width={130} height={38} radius={10} />
    </div>
    <div className="row g-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="col-12 col-md-6 col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
            <Skeleton height={120} radius={0} />
            <div className="card-body">
              <Skeleton width="65%" height={16} className="mb-2" />
              <Skeleton width="90%" height={12} className="mb-3" />
              <div className="d-flex gap-2">
                <Skeleton width={70} height={30} radius={8} />
                <Skeleton width={70} height={30} radius={8} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </Wrap>
);

export const PublicFormSkeleton = () => (
  <div className="container py-4" role="status" aria-label="Loading">
    <div className="card border-0 shadow-sm mx-auto overflow-hidden" style={{ borderRadius: 16, maxWidth: 640 }}>
      <Skeleton height={180} radius={0} />
      <div className="card-body p-4">
        <Skeleton width="60%" height={24} className="mb-2" />
        <Skeleton width="85%" height={12} className="mb-4" />
        <FormSkeleton fields={4} />
      </div>
    </div>
  </div>
);
